import axios from 'axios';
import type { BundleFloorBinDeltaBatch, BundleFloorLevel } from '../schemas/apiResponse.schemas';
import {
  BundleFloorBinDeltaBatchResponseSchema,
  BundleFloorEntryResponseSchema,
  BundleFloorListResponseSchema,
  BundleFloorRemoveResponseSchema,
} from '../schemas/apiResponse.schemas';
import { validateResponse } from './validateResponse';

/** One bin-scoped quantity change: a completion replace, credit, or a deferred pick decrement. */
export interface BundleFloorBinDelta {
  readonly sku: string;
  readonly storageIdPrefix: string | null;
  readonly quantity: number;
  readonly direction: 'increment' | 'decrement' | 'set_quantity';
}

const API_BASE = '/api/bundle-floor-levels';
const DEFAULT_TARGET_QTY = 0;
const inFlightBySku = new Map<string, Promise<BundleFloorLevel>>();

function skuPath(sku: string): string {
  return `${API_BASE}/${encodeURIComponent(sku)}`;
}

export async function fetchBundleFloorLevels(): Promise<BundleFloorLevel[]> {
  const response = await axios.get(API_BASE);
  const parsed = validateResponse(
    BundleFloorListResponseSchema,
    response.data,
    'fetchBundleFloorLevels'
  );
  return parsed.entries;
}

export async function fetchBundleFloorLevelBySku(sku: string): Promise<BundleFloorLevel> {
  const key = sku.trim();
  const pending = inFlightBySku.get(key);
  if (pending) {
    return pending;
  }

  const request = (async (): Promise<BundleFloorLevel> => {
    try {
      const response = await axios.get(skuPath(key));
      const parsed = validateResponse(
        BundleFloorEntryResponseSchema,
        response.data,
        'fetchBundleFloorLevelBySku',
      );
      return parsed.entry;
    } finally {
      inFlightBySku.delete(key);
    }
  })();

  inFlightBySku.set(key, request);
  return request;
}

export async function addBundleFloorParent(
  sku: string,
  targetQty: number = DEFAULT_TARGET_QTY,
): Promise<BundleFloorLevel> {
  const response = await axios.post(API_BASE, {
    sku: String(sku).trim(),
    target_qty: targetQty,
  });
  const parsed = validateResponse(
    BundleFloorEntryResponseSchema,
    response.data,
    'addBundleFloorParent'
  );
  return parsed.entry;
}

export async function removeBundleFloorParent(sku: string): Promise<void> {
  const response = await axios.delete(skuPath(sku));
  validateResponse(
    BundleFloorRemoveResponseSchema,
    response.data,
    'removeBundleFloorParent'
  );
}

export async function updateBundleFloorTargetQty(
  sku: string,
  targetQty: number,
): Promise<BundleFloorLevel> {
  const response = await axios.patch(skuPath(sku), { target_qty: targetQty });
  const parsed = validateResponse(
    BundleFloorEntryResponseSchema,
    response.data,
    'updateBundleFloorTargetQty',
  );
  return parsed.entry;
}

/** Absolute quantity for one pick bin (admin "Current" edit). */
export async function setBundleFloorBinQuantity(
  sku: string,
  storageIdPrefix: string,
  quantity: number,
): Promise<BundleFloorLevel> {
  const response = await axios.post(`${skuPath(sku)}/actions`, {
    action: 'set_quantity',
    quantity,
    storage_id_prefix: storageIdPrefix,
  });
  const parsed = validateResponse(
    BundleFloorEntryResponseSchema,
    response.data,
    'setBundleFloorBinQuantity',
  );
  return parsed.entry;
}

/**
 * Applies a batch of bin-scoped changes. Individual items can fail without discarding the
 * rest of the batch, so callers must inspect `results` rather than assuming success.
 */
export async function applyBundleFloorBinDeltas(
  items: readonly BundleFloorBinDelta[],
  source?: string,
): Promise<BundleFloorBinDeltaBatch> {
  const response = await axios.post(`${API_BASE}/bin-deltas`, {
    items: items.map((item) => ({
      sku: item.sku,
      ...(item.storageIdPrefix ? { storage_id_prefix: item.storageIdPrefix } : {}),
      quantity: item.quantity,
      direction: item.direction,
    })),
    ...(source ? { source } : {}),
  });
  return validateResponse(
    BundleFloorBinDeltaBatchResponseSchema,
    response.data,
    'applyBundleFloorBinDeltas',
  );
}
